import React, { useState } from 'react';
import { useStyletron } from 'baseui';
import { Input } from 'baseui/input';
import { Button } from 'baseui/button';
import { Notification, KIND } from 'baseui/notification';
import { gameplayGuardrailApi, GuardrailActionErrorCode } from './api';
import GameplayGuardrailReviewPanel from './GuardrailReviewPanel';

type ActionType = 'guardrailReview' | 'blockUser' | 'unblockUser';

interface NotificationState {
	message: string;
	kind: typeof KIND[keyof typeof KIND];
}

// A block/unblock writes two stores: the cognilens guardrail_actions record and
// the game_users enforcement flag that ludo actually reads. These codes say which
// half landed, so the operator knows whether a retry is safe.
const ERROR_CODE_MESSAGES: Record<GuardrailActionErrorCode, string> = {
	COGNILENS_WRITE_FAILED: 'Nothing was applied. Press Submit again to retry.',
	BLOCK_REVERTED: 'Nothing was applied — the guardrail record was rolled back. Press Submit again to retry.',
	GAME_USER_WRITE_FAILED:
		'The guardrail record is in place but gameplay enforcement was not applied. Press Submit again to finish it.',
	PARTIAL_UNBLOCK: 'Only partially unblocked. Press Submit again to finish it.',
	INCONSISTENT_STATE:
		'The guardrail record and gameplay enforcement are now out of sync for this user. Do NOT retry — escalate this user ID to the AI platform team.',
	USER_NOT_FOUND: 'No user found for this Zupee user ID. Check the ID and try again.'
};

// Delete Profile and Update Talktime are Talkzy-only concepts (they act on the
// companion product via the ai-chat-gateway) and are deliberately absent here.
// Block/Unblock are namespaced per product surface in cognilens, so these act
// only on gameplay enforcement state.
const ACTIONS: { id: ActionType; title: string; description: string }[] = [
	{
		id: 'guardrailReview',
		title: 'Guardrail Review',
		description: 'Review gameplay guardrail violations and enforcement actions'
	},
	{
		id: 'blockUser',
		title: 'Block User',
		description: "Block a user from the AI gameplay companion. Takes effect from the player's next game."
	},
	{
		id: 'unblockUser',
		title: 'Unblock User',
		description: 'Remove all active gameplay blocks for a user'
	}
];

const labelStyle = {
	fontSize: '13px',
	fontWeight: '500' as const,
	color: '#374151',
	display: 'block',
	marginBottom: '6px'
};

const GameplayGuardrailReviewDashboard: React.FC = () => {
	const [css] = useStyletron();
	const [activeAction, setActiveAction] = useState<ActionType>('guardrailReview');
	const [notification, setNotification] = useState<NotificationState | null>(null);
	const [loading, setLoading] = useState(false);
	const [userId, setUserId] = useState('');

	const handleNavClick = (id: ActionType) => {
		setActiveAction(id);
		setNotification(null);
		setUserId('');
	};

	const handleSubmit = async () => {
		if (!userId.trim()) {
			setNotification({ message: 'User ID is required', kind: KIND.negative });
			return;
		}
		setLoading(true);
		setNotification(null);
		try {
			const result =
				activeAction === 'blockUser'
					? await gameplayGuardrailApi.blockUser(userId)
					: await gameplayGuardrailApi.unblockUser(userId);

			if (result?.success) {
				setNotification({ message: result.data?.message || 'Action completed successfully', kind: KIND.positive });
				setUserId('');
			} else {
				const guidance = result?.errorCode ? ERROR_CODE_MESSAGES[result.errorCode] : undefined;
				setNotification({
					message: guidance || result?.error || 'Action failed',
					kind: KIND.negative
				});
			}
		} catch (e) {
			setNotification({ message: 'Network error. Please try again.', kind: KIND.negative });
		}
		setLoading(false);
	};

	const activeItem = ACTIONS.find((a) => a.id === activeAction);

	return (
		<div className={css({ display: 'flex', height: '100%', backgroundColor: '#f8f9fa' })}>
			{/* Sidebar */}
			<div
				className={css({
					width: '260px',
					minWidth: '260px',
					backgroundColor: '#fff',
					borderRight: '1px solid #e5e7eb',
					padding: '24px 0',
					display: 'flex',
					flexDirection: 'column'
				})}>
				<div className={css({ padding: '0 20px 20px', borderBottom: '1px solid #e5e7eb' })}>
					<h2 className={css({ fontSize: '15px', fontWeight: '700', color: '#111', margin: '0' })}>Gameplay Guardrails</h2>
					<p className={css({ fontSize: '12px', color: '#9ca3af', marginTop: '4px', marginBottom: '0' })}>
						Select an action
					</p>
				</div>

				<nav className={css({ padding: '12px 0' })}>
					{ACTIONS.map((action) => {
						const isActive = activeAction === action.id;
						return (
							<div
								key={action.id}
								role='button'
								tabIndex={0}
								onClick={() => handleNavClick(action.id)}
								className={css({
									padding: '12px 20px',
									cursor: 'pointer',
									backgroundColor: isActive ? '#f0f4ff' : 'transparent',
									borderLeft: isActive ? '3px solid #4f46e5' : '3px solid transparent',
									':hover': { backgroundColor: '#f9fafb' }
								})}>
								<div
									className={css({
										fontSize: '13px',
										fontWeight: isActive ? '600' : '400',
										color: isActive ? '#4f46e5' : '#374151'
									})}>
									{action.title}
								</div>
							</div>
						);
					})}
				</nav>
			</div>

			{/* Main Content */}
			<div className={css({ flex: '1', padding: '36px 40px', overflowY: 'auto' })}>
				{activeAction === 'guardrailReview' && <GameplayGuardrailReviewPanel />}
				{activeAction !== 'guardrailReview' && activeItem && (
					<>
						<h3 className={css({ fontSize: '20px', fontWeight: '600', color: '#111', marginBottom: '6px' })}>
							{activeItem.title}
						</h3>
						<p className={css({ fontSize: '14px', color: '#6b7280', marginBottom: '28px' })}>{activeItem.description}</p>

						{notification && (
							<div className={css({ marginBottom: '24px', maxWidth: '480px' })}>
								<Notification kind={notification.kind} onClose={() => setNotification(null)}>
									{notification.message}
								</Notification>
							</div>
						)}

						<div
							className={css({
								backgroundColor: '#fff',
								border: '1px solid #e5e7eb',
								borderRadius: '12px',
								padding: '28px',
								maxWidth: '480px'
							})}>
							<div className={css({ display: 'flex', flexDirection: 'column', gap: '16px' })}>
								<div>
									<label className={css(labelStyle)}>User ID *</label>
									<Input
										value={userId}
										onChange={(e) => setUserId(e.currentTarget.value)}
										placeholder='Enter zupee user ID'
									/>
									<p
										className={css({
											fontSize: '12px',
											color: '#6b7280',
											marginTop: '6px',
											marginBottom: '0'
										})}>
										Gameplay guardrail records are keyed by the numeric Zupee user ID.
									</p>
								</div>
								<div className={css({ marginTop: '4px' })}>
									<Button onClick={handleSubmit} isLoading={loading}>
										Submit
									</Button>
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default GameplayGuardrailReviewDashboard;
